import React, { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { Switch } from '../switch';
import { ConfirmDialog } from '../confirm-dialog';
import { ISummaPreview, ISummaPreviewRefusal, SummaPreviewRefusalCode } from '../../hooks/ai-search/use-chat';

export interface ISummaPreviewSwitchProps {
  offeredPreview?: ISummaPreview;
  preview?: ISummaPreview;
  hasAnswers: boolean;
  isLoading: boolean;
  onSwitch: (nextPreview?: ISummaPreview) => void;
}

// Shown while a preview is on even if it is no longer offered, so the superuser can always switch back.
export function SummaPreviewSwitch(props: ISummaPreviewSwitchProps) {
  const { offeredPreview, preview, hasAnswers, isLoading, onSwitch } = props;
  const [isConfirming, setIsConfirming] = useState(false);
  const shownPreview = preview ?? offeredPreview;

  if (!shownPreview) {
    return null;
  }

  function nextPreview() {
    return preview ? undefined : offeredPreview;
  }

  function handleChange() {
    if (hasAnswers) {
      setIsConfirming(true);
      return;
    }

    onSwitch(nextPreview());
  }

  function handleConfirm() {
    setIsConfirming(false);
    onSwitch(nextPreview());
  }

  return (
    <Box>
      <Switch label={`Prøv kommende model (${shownPreview.displayName})`} checked={!!preview} onChange={handleChange} disabled={isLoading} dense />
      <ConfirmDialog
        type="question"
        open={isConfirming}
        title={preview ? 'Skift tilbage til kommunens model?' : 'Skift til den kommende model?'}
        message="Den nuværende samtale slettes, og der startes en ny."
        confirmButtonText="Skift og ryd samtalen"
        cancelButtonText="Annuller"
        onConfirmClick={handleConfirm}
        onCancelClick={() => setIsConfirming(false)}
      />
    </Box>
  );
}

const refusalTexts: Record<SummaPreviewRefusalCode, string> = {
  SummaPreviewUnavailable:
    'Den kommende model kan ikke længere afprøves, så dit spørgsmål blev ikke besvaret. Det står i feltet igen – send det for at få svar fra kommunens model.',
  SummaConversationModelChanged: 'Samtalen blev startet med en anden model. Dit spørgsmål står i feltet igen – send det for at starte en ny samtale.',
  SummaPreviewNotAllowed: 'Du har ikke adgang til at afprøve den kommende model. Dit spørgsmål står i feltet igen – send det for at få svar fra kommunens model.',
};

export interface ISummaPreviewBannerProps {
  preview?: ISummaPreview;
  refusal?: ISummaPreviewRefusal;
  hasAnswers: boolean;
}

export function SummaPreviewBanner(props: ISummaPreviewBannerProps) {
  const { preview, refusal, hasAnswers } = props;

  if (refusal) {
    const forgottenConversation = hasAnswers ? ' Summa husker ikke samtalen ovenfor.' : '';
    return (
      <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
        {refusalTexts[refusal.systemCode] + forgottenConversation}
      </Alert>
    );
  }

  if (preview) {
    return (
      <Alert severity="info" role="status" sx={{ mx: 2, mb: 1 }}>
        {`Du afprøver den kommende model ${preview.displayName}. Kommunen bruger den endnu ikke, så svarene kan afvige fra dem, dine kolleger får.`}
      </Alert>
    );
  }

  return null;
}
